import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useContext, useEffect, useState } from "react";
import { CMSContext } from "../components/CaseManagement/context/CaseManagmentContext";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import { Delete as DeleteIcon } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { uploadDocumentApi, updateDocumentApi, getFileAsBlobApi } from "../services/api/caseManagementApi";

export const UploadDocument = ({
  uploadDialogOpen,
  setUploadDialogOpen,
  editDocument,
  setEditingDocument,
}) => {
  const { selectedCase, fetchDocuments } = useContext(CMSContext);
  const [loading, setLoading] = useState(false);
  const [gettingFile, setGettingFile] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({
    title: "",
    description: "",
    file: "",
  });
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const userId = useSelector((state) => state.credit.userId);
  const [newDocument, setNewDocument] = useState({
    title: "",
    description: "",
    parentId: null,
  });
  const [originalEditFileUrl, setOriginalEditFileUrl] = useState(null);

  useEffect(() => {
    setOriginalEditFileUrl(editDocument?.fileUrl || null);
  }, [editDocument?._id]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchFileDetails = async () => {
      try {
        if (editDocument?.fileUrl) {
          const fileDetails = await getFileDetailsWithBlob(
            editDocument.fileUrl,
            abortController.signal
          );

          if (isMounted) {
            const file = new File([fileDetails.blob], fileDetails.fileName, {
              type: fileDetails.fileType,
            });

            setNewDocument({ ...editDocument, ...fileDetails });
            setAttachments([{ file }]);
          }
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching file details:", error);
          toast.error("Failed to load document details.");
        }
      }
    };

    fetchFileDetails();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [editDocument]);

  const onDrop = async (acceptedFiles) => {
    const formattedAttachments = await Promise.all(
      acceptedFiles?.map(async (file) => {
        return { file };
      })
    );

    setAttachments((prev) => [...formattedAttachments]);
  };

  const onDropRejected = (fileRejections) => {
    const allowedTypes = {
      "application/pdf": 3 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 5 * 1024 * 1024,
      "image/jpeg": 3 * 1024 * 1024,
      "image/jpg": 3 * 1024 * 1024,
      "image/png": 3 * 1024 * 1024,
    };

    const errorMessages = new Set();

    fileRejections.forEach(({ file, errors }) => {
      errors.forEach((e) => {
        if (e.code === "file-too-large") {
          const maxSize = allowedTypes[file.type];
          const formattedSize = maxSize ? formatBytes(maxSize) : "unknown size";
          errorMessages.add(
            `File "${file.name}" exceeds the maximum allowed size of ${formattedSize}.`
          );
        } else if (e.code === "file-invalid-type") {
          errorMessages.add(`Unsupported file type: ${file.name}`);
        } else if (e.code === "too-many-files") {
          errorMessages.add("You can only upload up to 1 file.");
        } else {
          errorMessages.add(`Error uploading file "${file.name}": ${e.message}`);
        }
      });
    });

    errorMessages.forEach((message) => {
      toast.error(message);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "image/*": [".jpeg", ".jpg", ".png"],
    },
    maxFiles: 1,
  });

  const handleRemoveFile = () => {
    setAttachments([]);
    setNewDocument((prev) => ({
      ...prev,
      fileUrl: null,
      fileName: null,
    }));
    setEditingDocument((prev) => ({
      ...prev,
      fileUrl: null,
    }));
  };

  const handleDocumentUpload = async () => {
    const newErrors = {
      title: !newDocument.title ? "Title is required" : "",
      description: !newDocument.description ? "Description is required" : "",
    };

    if (Object.values(newErrors).some((error) => error !== "")) {
      setErrors(newErrors);
      return;
    }

    if (!attachments?.length && !editDocument?.fileUrl && !originalEditFileUrl) {
      toast.error("Please select a document");
      return;
    }

    try {
      const isEditMode = !!editDocument?._id;
      const nextVersion = isEditMode
        ? `${(parseInt(String(editDocument?.version || newDocument?.version || "1").split(".")[0]) || 1) + 1}.0`
        : "1.0";

      const formData = new FormData();
      formData.append("title", newDocument.title);
      formData.append("description", newDocument.description);

      if (attachments?.length > 0) {
        formData.append("file", attachments[0]?.file);
      }

      formData.append("version", nextVersion);
      if (newDocument.parentId) formData.append("parentId", newDocument.parentId);
      if (userId) formData.append("uploadedBy", userId);
      if (userInfo?.name) formData.append("uploadedByName", userInfo.name);

      setLoading(true);
      let response;

      if (isEditMode) {
        response = await updateDocumentApi(selectedCase?._id, editDocument?._id, formData);
      } else {
        response = await uploadDocumentApi(selectedCase?._id, formData);
      }

      if (response.status === 201 || response.status === 200) {
        await fetchDocuments();

        setUploadDialogOpen(false);
        setNewDocument({ title: "", description: "", parentId: null });
        setAttachments([]);
        setOriginalEditFileUrl(null);
        setLoading(false);

        toast.success(
          isEditMode ? "Document updated successfully" : "Document uploaded successfully"
        );
      }
    } catch (error) {
      console.error("Document operation failed:", error);
      setLoading(false);
      toast.error(
        error.response?.data?.message ||
          `Document ${editDocument?._id ? "update" : "upload"} failed`
      );
    }
  };

  const getFileDetailsWithBlob = async (fileUrl, signal) => {
    try {
      setGettingFile(true);
      const response = await getFileAsBlobApi(fileUrl, signal);
      if (response.status !== 200) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = new Blob([response.data]);
      setGettingFile(false);
      return {
        fileName: extractFileName(fileUrl),
        fileSize: blob.size,
        fileType: blob.type,
        lastModified: response.headers.get("Last-Modified"),
        blob,
      };
    } catch (error) {
      if (error.name !== "AbortError") {
        setGettingFile(false);
        console.error("Failed to fetch file details:", error);
        throw error;
      }
    }
  };

  const extractFileName = (url) => {
    const parts = url.split("/");
    const fileNameWithUUID = parts[parts.length - 1];
    const fileNameParts = fileNameWithUUID.split("-");
    return fileNameParts[fileNameParts.length - 1];
  };

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          boxShadow: 24,
          p: { xs: 0, md: 1 },
          width: 500,
        },
      }}
      open={uploadDialogOpen}
      onClose={() => {
        setUploadDialogOpen(false);
        setEditingDocument(null);
        setNewDocument({ title: "", description: "", parentId: null });
        setAttachments([]);
        setOriginalEditFileUrl(null);
        setErrors({ title: "", description: "", file: "" });
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontSize: "18px",
          fontWeight: "bold",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {editDocument?._id ? "Edit Uploaded Document" : "Upload Document"}
      </DialogTitle>
      <DialogContent sx={{ mt: "12px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "4px",
            paddingTop: "6px",
          }}
        >
          <TextField
            label="Document Title"
            fullWidth
            size="small"
            value={newDocument.title}
            required
            error={!!errors.title}
            helperText={errors.title}
            onChange={(e) =>
              setNewDocument({ ...newDocument, title: e.target.value })
            }
            InputProps={{
              sx: {
                bgcolor: "white",
                fontSize: "14px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                },
              },
            }}
            InputLabelProps={{ sx: { fontSize: "14px" } }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            required
            error={!!errors.description}
            helperText={errors.description}
            size="small"
            value={newDocument.description}
            onChange={(e) =>
              setNewDocument({ ...newDocument, description: e.target.value })
            }
            InputProps={{
              sx: {
                bgcolor: "white",
                fontSize: "14px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                },
              },
            }}
            InputLabelProps={{ sx: { fontSize: "14px" } }}
          />

          {!(editDocument?.fileUrl || attachments?.length > 0) && (
            <Box
              {...getRootProps()}
              sx={{
                width: "94%",
                border: "2px dashed #ccc",
                borderRadius: "8px",
                padding: 1.5,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: isDragActive ? "#f0f8ff" : "#fafafa",
                "&:hover": { borderColor: "#2196f3" },
                transition: "background-color 0.3s, border-color 0.3s",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: "#303f9f", mb: 1 }}>
                Attachments
              </Typography>
              <input {...getInputProps()} />
              <Typography>
                {isDragActive
                  ? "Drop files here..."
                  : "Click to upload files, or drag & drop files here"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (Only images, docx or PDFs. Max 1 file)
              </Typography>
            </Box>
          )}
          {(attachments?.length > 0 || editDocument?.fileUrl) && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: "#303f9f", fontSize: "14px" }}
              >
                Selected Files:
              </Typography>
              {gettingFile ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <CircularProgress size={18} sx={{ color: "#003D78" }} />
                  <Box sx={{ fontFamily: "Roboto, Helvetica, Arial, sans-serif" }}>
                    Loading...
                  </Box>
                </Box>
              ) : (
                <>
                  {attachments?.map((file, index) => (
                    <FilePreview
                      key={index}
                      file={file}
                      onRemove={() => handleRemoveFile(index)}
                    />
                  ))}

                  {!attachments?.length && editDocument?.fileUrl && (
                    <FilePreview
                      file={{
                        name: editDocument.fileName,
                        size: editDocument.fileSize,
                        type: editDocument.fileType,
                        preview: editDocument.fileUrl,
                      }}
                      onRemove={handleRemoveFile}
                    />
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            borderRadius: "24px",
            padding: "8px 24px",
            textTransform: "none",
            fontWeight: "bold",
          }}
          variant="outlined"
          color="error"
          onClick={() => {
            setUploadDialogOpen(false);
            setEditingDocument(null);
            setNewDocument({ title: "", description: "", parentId: null });
            setAttachments([]);
            setErrors({ title: "", description: "", file: "" });
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            fontSize: { xs: "13px", lg: "14px" },
            borderRadius: "24px",
            padding: "8px 24px",
            textTransform: "none",
            fontWeight: "bold",
            backgroundColor: "#003D78",
            "&:hover": { backgroundColor: "primary.main" },
            transition: "background-color 0.3s",
          }}
          disabled={loading}
          onClick={() => {
            handleDocumentUpload();
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <CircularProgress size={18} sx={{ color: "#a9a9a9" }} />
              <Box>Uploading...</Box>
            </Box>
          ) : (
            "Upload"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FilePreview = ({ file, onRemove }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ p: 1, border: "1px solid #ccc", borderRadius: "4px", mt: 1 }}
    >
      {file?.file?.type?.startsWith("image/") && (
        <Box
          component="img"
          src={file?.file?.preview || URL.createObjectURL(file?.file)}
          alt={file?.file?.name}
          sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: "4px" }}
        />
      )}
      {file?.file?.type?.startsWith("video/") && (
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: "#000",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "4px",
          }}
        >
          🎥
        </Box>
      )}
      {file?.file?.type === "application/pdf" && (
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: "#f44336",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "4px",
          }}
        >
          <PictureAsPdfIcon />
        </Box>
      )}
      {[
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(file?.file?.type) && (
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: "#1976d2",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "4px",
          }}
        >
          <DescriptionIcon />
        </Box>
      )}

      <Box flexGrow={1} mx={2}>
        <Typography
          variant="body2"
          sx={{
            maxWidth: { xs: "4rem", sm: "8rem", md: "10rem", lg: "13rem" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {file?.file?.name}
        </Typography>
        <Typography variant="caption">
          {(file?.file?.size / 1024).toFixed(2)} KB
        </Typography>
      </Box>

      <IconButton sx={{ color: "#f44336" }} onClick={onRemove}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default UploadDocument;
